import type {
  Activity,
  ActivityCreate,
  ActivityKind,
  ActivityRelatedType,
  ActivityUpdate,
  Contact,
  CrmAccount,
  Lead,
  Opportunity,
} from '@erp/shared';
import { Activity as ActivityIcon, CheckCircle2, Pencil, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Badge, Card, Input, Skeleton } from '../../components/shadcn/index.js';
import {
  Button,
  confirmDialog,
  DataTable,
  type DataTableColumn,
  EmptyState,
  FormField,
} from '../../components/ui/index.js';
import { usePermission } from '../../hooks/usePermission.js';
import { api } from '../../lib/api.js';
import { formatDateTime } from '../../lib/intl.js';

const KIND_VARIANT: Record<ActivityKind, 'info' | 'warning' | 'success' | 'secondary'> = {
  call: 'info',
  email: 'info',
  meeting: 'warning',
  note: 'secondary',
  task: 'success',
};

const KINDS: ActivityKind[] = ['call', 'email', 'meeting', 'note', 'task'];
const RELATED_TYPES: ActivityRelatedType[] = ['lead', 'contact', 'account', 'opportunity'];

type CompletedFilter = 'open' | 'completed' | 'all';

/**
 * Lookup entry for the related-record column. Activities link via
 * { relatedType, relatedId } across four entity types; we want to
 * show a human-readable label per row instead of a UUID prefix.
 */
interface RelatedEntry {
  type: ActivityRelatedType;
  label: string;
}

export function ActivitiesPage(): JSX.Element {
  const { t, i18n } = useTranslation('crm');
  const { t: tc } = useTranslation();
  const canWrite = usePermission('crm.activities.write');
  const [items, setItems] = useState<Activity[] | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<Activity | null>(null);
  const [kindFilter, setKindFilter] = useState<ActivityKind | ''>('');
  const [completedFilter, setCompletedFilter] = useState<CompletedFilter>('open');
  // One-shot directories so the related-record column can resolve
  // names. Each fetch is best-effort: a 403 / network blip on one
  // type falls back to the UUID stub for that type's rows while the
  // other types keep working.
  const [leads, setLeads] = useState<Lead[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [accounts, setAccounts] = useState<CrmAccount[]>([]);
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);

  const reload = async (): Promise<void> => {
    const params = new URLSearchParams();
    if (kindFilter) params.set('kind', kindFilter);
    if (completedFilter === 'open') params.set('completed', 'false');
    if (completedFilter === 'completed') params.set('completed', 'true');
    const qs = params.toString();
    const url = qs.length > 0 ? `/crm/activities?${qs}` : '/crm/activities';
    const r = await api().get<{ items: Activity[] }>(url);
    setItems(r.data.items);
  };

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kindFilter, completedFilter]);

  // Pull the four related-entity directories once on mount. Each is
  // wrapped in its own try/catch so a permission denial on one type
  // doesn't blank-out the lookups for the others.
  useEffect(() => {
    const fetchOne = async <T,>(url: string, setter: (v: T[]) => void): Promise<void> => {
      try {
        const r = await api().get<{ items: T[] }>(url);
        setter(r.data.items);
      } catch {
        // Best-effort: leave the slot empty; the UUID-prefix fallback
        // covers rows whose related entity didn't load.
      }
    };
    void fetchOne<Lead>('/crm/leads', setLeads);
    void fetchOne<Contact>('/crm/contacts?status=active', setContacts);
    void fetchOne<CrmAccount>('/crm/accounts?status=active', setAccounts);
    void fetchOne<Opportunity>('/crm/opportunities', setOpportunities);
  }, []);

  // Unified id → { type, label } lookup so the row renderer can pull
  // the human-readable string with one Map.get() call. Each entity
  // type contributes its own label format:
  //   * Lead / Contact → displayName
  //   * Account / Opportunity → "code — name"
  const relatedById = useMemo(() => {
    const m = new Map<string, RelatedEntry>();
    for (const l of leads) m.set(l.id, { type: 'lead', label: l.displayName });
    for (const c of contacts) m.set(c.id, { type: 'contact', label: c.displayName });
    for (const a of accounts) m.set(a.id, { type: 'account', label: `${a.code} — ${a.name}` });
    for (const o of opportunities) m.set(o.id, { type: 'opportunity', label: `${o.code} — ${o.name}` });
    return m;
  }, [leads, contacts, accounts, opportunities]);

  const completeOne = async (id: string): Promise<void> => {
    await api().post(`/crm/activities/${id}/complete`, {});
    await reload();
  };

  const openEdit = (a: Activity): void => {
    setShowCreate(false);
    setEditing(a);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const onDelete = async (a: Activity): Promise<void> => {
    if (!(await confirmDialog({ message: t('activities.actions.confirmDelete', { subject: a.subject }), tone: 'danger' }))) return;
    try {
      await api().delete(`/crm/activities/${a.id}`);
      await reload();
    } catch {
      toast.error(tc('errors:server'));
    }
  };

  if (!items) {
    return (
      <Card className="p-6">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="mt-4 h-32 w-full" />
      </Card>
    );
  }

  const fmtDate = (iso: string | null): string => {
    if (!iso) return t('activities.noDueDate');
    return formatDateTime(iso, { locale: i18n.language });
  };

  const isOverdue = (a: Activity): boolean => {
    return a.completedAt === null && a.dueAt !== null && new Date(a.dueAt).getTime() < Date.now();
  };

  const columns: DataTableColumn<Activity>[] = [
    {
      id: 'kind',
      header: t('activities.columns.kind'),
      sortValue: (a) => t(`activities.kind.${a.kind}`),
      searchValue: (a) => t(`activities.kind.${a.kind}`),
      cell: (a) => <Badge variant={KIND_VARIANT[a.kind]}>{t(`activities.kind.${a.kind}`)}</Badge>,
    },
    {
      id: 'subject',
      header: t('activities.columns.subject'),
      sortValue: (a) => a.subject,
      searchValue: (a) => a.subject,
      cellClassName: 'font-medium',
      cell: (a) => a.subject,
    },
    {
      id: 'related',
      header: t('activities.columns.related'),
      sortValue: (a) => relatedById.get(a.relatedId)?.label ?? a.relatedId,
      searchValue: (a) => relatedById.get(a.relatedId)?.label ?? a.relatedId,
      cellClassName: 'text-xs',
      cell: (a) => (
        <>
          <span className="text-fg-muted">{t(`activities.relatedType.${a.relatedType}`)}</span>
          {' · '}
          {relatedById.get(a.relatedId)?.label ? (
            <span className="font-medium">{relatedById.get(a.relatedId)!.label}</span>
          ) : (
            // Directory missing or fetch failed → short UUID
            // prefix keeps the row identifiable.
            <span className="font-mono text-fg-muted">{a.relatedId.slice(0, 8)}</span>
          )}
        </>
      ),
    },
    {
      id: 'dueAt',
      header: t('activities.columns.dueAt'),
      sortValue: (a) => a.dueAt ?? '',
      cellClassName: 'font-mono text-xs',
      cell: (a) => (
        <>
          {fmtDate(a.dueAt)}
          {isOverdue(a) && (
            <Badge variant="danger" className="ms-2">
              {t('activities.overdue')}
            </Badge>
          )}
        </>
      ),
    },
    {
      id: 'status',
      header: t('activities.columns.status'),
      sortValue: (a) => (a.completedAt ? 1 : 0),
      cell: (a) =>
        a.completedAt ? (
          <Badge variant="success">{t('activities.status.completed')}</Badge>
        ) : (
          <Badge variant="info">{t('activities.status.open')}</Badge>
        ),
    },
    ...(canWrite
      ? [
          {
            id: 'actions',
            header: '',
            align: 'end' as const,
            headClassName: 'w-32',
            cell: (a: Activity) => (
              <div className="flex justify-end gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => openEdit(a)}
                  aria-label={`${t('activities.actions.edit')} ${a.subject}`}
                  title={t('activities.actions.edit')}
                >
                  <Pencil className="h-4 w-4" aria-hidden />
                </Button>
                {!a.completedAt && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      void completeOne(a.id);
                    }}
                    title={t('activities.complete')}
                  >
                    <CheckCircle2 className="h-4 w-4" aria-hidden />
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onDelete(a)}
                  aria-label={`${t('activities.actions.delete')} ${a.subject}`}
                  title={t('activities.actions.delete')}
                >
                  <Trash2 className="h-4 w-4" aria-hidden />
                </Button>
              </div>
            ),
          },
        ]
      : []),
  ];

  return (
    <div className="flex flex-col gap-4">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">{t('activities.title')}</h1>
          <p className="mt-1 text-sm text-fg-muted">{t('activities.description')}</p>
        </div>
        {canWrite && !showCreate && !editing && (
          <Button onClick={() => setShowCreate(true)}>{t('activities.create.title')}</Button>
        )}
      </header>

      {showCreate && (
        <CreateActivityForm
          dirs={{ leads, contacts, accounts, opportunities }}
          onCancel={() => setShowCreate(false)}
          onCreated={async () => {
            setShowCreate(false);
            await reload();
          }}
        />
      )}

      {editing && (
        <EditActivityForm
          activity={editing}
          dirs={{ leads, contacts, accounts, opportunities }}
          onCancel={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await reload();
          }}
        />
      )}

      <div className="flex flex-wrap items-center gap-3">
        <select
          className="rounded-md border border-border bg-bg px-3 py-2 text-sm text-fg"
          value={kindFilter}
          onChange={(e) => setKindFilter(e.target.value as ActivityKind | '')}
          aria-label={t('activities.kindFilter')}
        >
          <option value="">{t('activities.allKinds')}</option>
          {KINDS.map((k) => (<option key={k} value={k}>{t(`activities.kind.${k}`)}</option>))}
        </select>
        <select
          className="rounded-md border border-border bg-bg px-3 py-2 text-sm text-fg"
          value={completedFilter}
          onChange={(e) => setCompletedFilter(e.target.value as CompletedFilter)}
          aria-label={t('activities.completedFilter')}
        >
          <option value="open">{t('activities.openOnly')}</option>
          <option value="completed">{t('activities.completedOnly')}</option>
          <option value="all">{t('activities.all')}</option>
        </select>
      </div>

      {items.length === 0 && !showCreate ? (
        <EmptyState
          icon={<ActivityIcon className="h-8 w-8" aria-hidden />}
          title={t('activities.empty')}
          description={t('activities.emptyDescription')}
          action={
            canWrite ? (
              <Button size="sm" onClick={() => setShowCreate(true)}>
                {t('activities.create.title')}
              </Button>
            ) : undefined
          }
        />
      ) : (
        <Card className="overflow-hidden p-0">
          <DataTable columns={columns} data={items} rowKey={(a) => a.id} />
        </Card>
      )}
    </div>
  );
}

interface Directories {
  leads: Lead[];
  contacts: Contact[];
  accounts: CrmAccount[];
  opportunities: Opportunity[];
}

interface RelatedOption {
  id: string;
  label: string;
}

/**
 * Map an ActivityRelatedType to the right directory list, with a
 * `label` already formatted for each option. Lead/Contact use
 * displayName; Account/Opportunity use "code — name" so two accounts
 * with the same name are still disambiguated.
 */
function optionsForType(
  type: ActivityRelatedType,
  dirs: Directories,
): RelatedOption[] {
  switch (type) {
    case 'lead':
      return dirs.leads.map((l) => ({ id: l.id, label: l.displayName }));
    case 'contact':
      return dirs.contacts.map((c) => ({ id: c.id, label: c.displayName }));
    case 'account':
      return dirs.accounts.map((a) => ({ id: a.id, label: `${a.code} — ${a.name}` }));
    case 'opportunity':
      return dirs.opportunities.map((o) => ({ id: o.id, label: `${o.code} — ${o.name}` }));
  }
}

interface CreateFormProps {
  dirs: Directories;
  onCancel: () => void;
  onCreated: () => void | Promise<void>;
}

function CreateActivityForm({ dirs, onCancel, onCreated }: CreateFormProps): JSX.Element {
  const { t } = useTranslation('crm');
  const { t: tc } = useTranslation();
  // Pick the first related-type that actually has at least one record.
  // Without this the form would open on 'lead' even for a tenant that
  // hasn't captured any leads yet, leaving the relatedId dropdown
  // disabled + the form unsavable until the operator notices they
  // need to change type. Falls back to 'lead' when every directory
  // is empty (greenfield tenant) — the disabled-select state then
  // communicates "you need to create a record first".
  const initialRelatedType: ActivityRelatedType =
    RELATED_TYPES.find((type) => optionsForType(type, dirs).length > 0) ?? 'lead';
  const initialOptions = optionsForType(initialRelatedType, dirs);
  const [form, setForm] = useState<ActivityCreate>({
    kind: 'call',
    subject: '',
    relatedType: initialRelatedType,
    relatedId: initialOptions[0]?.id ?? '',
  });
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const options = optionsForType(form.relatedType, dirs);

  // When the operator flips relatedType, the prior relatedId belongs
  // to a different entity type and would 404 on save. Reset to the
  // first available record of the new type (or empty if there are none).
  const onTypeChange = (type: ActivityRelatedType): void => {
    setForm((f) => ({
      ...f,
      relatedType: type,
      relatedId: optionsForType(type, dirs)[0]?.id ?? '',
    }));
  };

  const onSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const payload: ActivityCreate = {
        ...form,
        dueAt: form.dueAt && form.dueAt.length > 0 ? new Date(form.dueAt).toISOString() : null,
      };
      await api().post('/crm/activities', payload);
      await onCreated();
    } catch {
      setError(tc('errors:server'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="p-6">
      <h2 className="mb-4 text-lg font-semibold">{t('activities.create.title')}</h2>
      <form onSubmit={onSubmit} className="grid grid-cols-1 gap-4 md:grid-cols-2" noValidate>
        <FormField id="act-kind" label={t('activities.create.kind')} required>
          <select
            id="act-kind"
            className="w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-fg"
            value={form.kind}
            onChange={(e) => setForm({ ...form, kind: e.target.value as ActivityKind })}
          >
            {KINDS.map((k) => (<option key={k} value={k}>{t(`activities.kind.${k}`)}</option>))}
          </select>
        </FormField>
        <FormField id="act-subject" label={t('activities.create.subject')} required>
          <Input id="act-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} required autoFocus />
        </FormField>
        <FormField id="act-related-type" label={t('activities.create.relatedType')} required>
          <select
            id="act-related-type"
            className="w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-fg"
            value={form.relatedType}
            onChange={(e) => onTypeChange(e.target.value as ActivityRelatedType)}
          >
            {RELATED_TYPES.map((r) => (<option key={r} value={r}>{t(`activities.relatedType.${r}`)}</option>))}
          </select>
        </FormField>
        <FormField id="act-related-id" label={t('activities.create.relatedId')} required>
          <select
            id="act-related-id"
            className="w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-fg"
            value={form.relatedId}
            onChange={(e) => setForm({ ...form, relatedId: e.target.value })}
            required
            disabled={options.length === 0}
          >
            {options.length === 0 ? (
              <option value="">—</option>
            ) : (
              options.map((o) => (
                <option key={o.id} value={o.id}>{o.label}</option>
              ))
            )}
          </select>
        </FormField>
        <FormField id="act-due" label={t('activities.create.dueAt')}>
          <Input
            id="act-due"
            type="datetime-local"
            value={form.dueAt ?? ''}
            onChange={(e) => setForm({ ...form, dueAt: e.target.value })}
          />
        </FormField>
        <div className="md:col-span-2">
          <FormField id="act-body" label={t('activities.create.body')}>
            <textarea
              id="act-body"
              className="w-full min-h-[80px] rounded-md border border-border bg-bg px-3 py-2 text-sm text-fg"
              value={form.body ?? ''}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
            />
          </FormField>
        </div>
        {error && <p className="md:col-span-2 text-sm text-danger" role="alert">{error}</p>}
        <div className="md:col-span-2 flex gap-2">
          <Button type="submit" loading={saving}>{tc('actions.create')}</Button>
          <Button type="button" variant="secondary" onClick={onCancel} disabled={saving}>{tc('actions.cancel')}</Button>
        </div>
      </form>
    </Card>
  );
}

// ---------------------------------------------------------------------------

interface EditActivityFormProps {
  activity: Activity;
  dirs: Directories;
  onCancel: () => void;
  onSaved: () => void | Promise<void>;
}

/**
 * Mirrors CreateActivityForm. Same field set — completedAt isn't
 * surfaced as a field because the existing "Complete" button is the
 * one-click path to set it, and editing a completed activity should
 * stay rare (the row is mostly historical at that point).
 *
 * If the activity's related entity got deleted/deactivated since the
 * row was created, the dropdown injects a fallback option (showing
 * the short UUID) so the form doesn't silently lose the assignment.
 */
function EditActivityForm({ activity, dirs, onCancel, onSaved }: EditActivityFormProps): JSX.Element {
  const { t } = useTranslation('crm');
  const { t: tc } = useTranslation();
  // Convert the server ISO timestamp back into the local `datetime-local`
  // input format (YYYY-MM-DDTHH:mm) the create form uses. Empty when null.
  const initialDueLocal = activity.dueAt
    ? toDatetimeLocalInputValue(activity.dueAt)
    : '';
  const [form, setForm] = useState<ActivityUpdate>({
    kind: activity.kind,
    subject: activity.subject,
    body: activity.body,
    relatedType: activity.relatedType,
    relatedId: activity.relatedId,
    dueAt: initialDueLocal,
  });
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  // Options for the current relatedType. If the activity's existing
  // relatedId isn't in that list (entity was deleted or filtered out),
  // inject a fallback "<short-uuid> (missing)" entry so the form
  // doesn't silently drop the assignment when the operator hits Save
  // without touching the picker.
  const baseOptions = optionsForType(form.relatedType ?? activity.relatedType, dirs);
  const options =
    form.relatedId && !baseOptions.some((o) => o.id === form.relatedId)
      ? [{ id: form.relatedId, label: `${form.relatedId.slice(0, 8)}… (missing)` }, ...baseOptions]
      : baseOptions;

  // Reset relatedId when the type flips (the old id belongs to a
  // different entity type and would 404).
  const onTypeChange = (type: ActivityRelatedType): void => {
    setForm((f) => ({
      ...f,
      relatedType: type,
      relatedId: optionsForType(type, dirs)[0]?.id ?? '',
    }));
  };

  const onSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const patch: ActivityUpdate = {
        kind: form.kind,
        subject: form.subject?.trim() || undefined,
        // Empty body collapses to null (server clears the column).
        body: form.body && form.body.trim() ? form.body.trim() : null,
        relatedType: form.relatedType,
        relatedId: form.relatedId,
        dueAt: form.dueAt && form.dueAt.length > 0 ? new Date(form.dueAt).toISOString() : null,
      };
      await api().patch(`/crm/activities/${activity.id}`, patch);
      await onSaved();
    } catch (err) {
      const code = (err as { response?: { data?: { code?: string } } }).response?.data?.code;
      setError(code === 'unprocessable' ? t('activities.errors.unprocessable') : tc('errors:server'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="p-6">
      <h2 className="mb-4 text-lg font-semibold">
        {t('activities.edit.title', { subject: activity.subject })}
      </h2>
      <form onSubmit={onSubmit} className="grid grid-cols-1 gap-4 md:grid-cols-2" noValidate>
        <FormField id="acte-kind" label={t('activities.create.kind')} required>
          <select
            id="acte-kind"
            className="w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-fg"
            value={form.kind ?? 'call'}
            onChange={(e) => setForm({ ...form, kind: e.target.value as ActivityKind })}
          >
            {KINDS.map((k) => (<option key={k} value={k}>{t(`activities.kind.${k}`)}</option>))}
          </select>
        </FormField>
        <FormField id="acte-subject" label={t('activities.create.subject')} required>
          <Input
            id="acte-subject"
            value={form.subject ?? ''}
            onChange={(e) => setForm({ ...form, subject: e.target.value })}
            required
            autoFocus
          />
        </FormField>
        <FormField id="acte-related-type" label={t('activities.create.relatedType')} required>
          <select
            id="acte-related-type"
            className="w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-fg"
            value={form.relatedType ?? 'lead'}
            onChange={(e) => onTypeChange(e.target.value as ActivityRelatedType)}
          >
            {RELATED_TYPES.map((r) => (<option key={r} value={r}>{t(`activities.relatedType.${r}`)}</option>))}
          </select>
        </FormField>
        <FormField id="acte-related-id" label={t('activities.create.relatedId')} required>
          <select
            id="acte-related-id"
            className="w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-fg"
            value={form.relatedId ?? ''}
            onChange={(e) => setForm({ ...form, relatedId: e.target.value })}
            required
            disabled={options.length === 0}
          >
            {options.length === 0 ? (
              <option value="">—</option>
            ) : (
              options.map((o) => (
                <option key={o.id} value={o.id}>{o.label}</option>
              ))
            )}
          </select>
        </FormField>
        <FormField id="acte-due" label={t('activities.create.dueAt')}>
          <Input
            id="acte-due"
            type="datetime-local"
            value={form.dueAt ?? ''}
            onChange={(e) => setForm({ ...form, dueAt: e.target.value })}
          />
        </FormField>
        <div className="md:col-span-2">
          <FormField id="acte-body" label={t('activities.create.body')}>
            <textarea
              id="acte-body"
              className="w-full min-h-[80px] rounded-md border border-border bg-bg px-3 py-2 text-sm text-fg"
              value={form.body ?? ''}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
            />
          </FormField>
        </div>
        {error && <p className="md:col-span-2 text-sm text-danger" role="alert">{error}</p>}
        <div className="md:col-span-2 flex gap-2">
          <Button type="submit" loading={saving}>{t('activities.edit.submit')}</Button>
          <Button type="button" variant="secondary" onClick={onCancel} disabled={saving}>
            {t('activities.edit.cancel')}
          </Button>
        </div>
      </form>
    </Card>
  );
}

/**
 * Format an ISO timestamp into the `YYYY-MM-DDTHH:mm` shape that
 * `<input type="datetime-local">` expects. Always uses the browser's
 * local timezone (same convention the create form's datetime input
 * already follows when the user types into it).
 */
function toDatetimeLocalInputValue(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
